import {FetchPolicy, gql} from '@apollo/client';
import {useRef} from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {
  ProcedureCheckValidationQuery,
  ProcedureCheckValidationQueryVariables,
} from './__generated/ProcedureCheckValidationQuery';

export const PROCEDURE_CHECK_VALIDATION_QUERY = gql`
  query ProcedureCheckValidationQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedure(id: $id) {
      ... on ProcedureNode {
        id
        editUrl
        invalidReasonsExpensive
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export enum ProcedureValidationStatus {
  EMPTY = 'EMPTY',
  IN_PROGRESS = 'IN_PROGRESS',
  VALID = 'VALID',
  INVALID = 'INVALID',
}

export interface ProcedureValidation {
  id: string | null;
  editUrl: string;
  validationStatus: ProcedureValidationStatus;
}

export function parseProcedureForValidation(queryResult: ProcedureCheckValidationQuery): ProcedureValidation {
  const {procedure} = queryResult;
  assert(!isInaccessible(procedure), 'Procedure must be accessible.');
  assert(procedure.id != null, 'Procedure ID must exist.');
  assert(procedure.editUrl != null, 'Procedure URL must exist.');
  return {
    id: procedure.id,
    editUrl: procedure.editUrl,
    validationStatus:
      procedure?.invalidReasonsExpensive?.length === 0
        ? ProcedureValidationStatus.VALID
        : ProcedureValidationStatus.INVALID,
  };
}

/**
 * Hook to manage validating if a procedure can currently be executed.
 *
 * @returns a {@link ProcedureValidation} for the given `procedureId` with the current validation status.
 * - EMPTY if the procedureId is null
 * - IN_PROGRESS if the procedure validation is still loading
 * - INVALID if the procedure is invalid for execution
 * - VALID if the procedure is valid for execution
 *
 * @package exported for testing
 */
export default function useProcedureValidation(
  procedureId: string | null,
  apolloFetchPolicy: FetchPolicy = 'cache-only'
): ProcedureValidation {
  const result = useSkippableBenchlingQuery<
    ProcedureCheckValidationQuery,
    ProcedureCheckValidationQueryVariables
  >(PROCEDURE_CHECK_VALIDATION_QUERY, {
    skip: procedureId == null,
    variables: {id: procedureId!},
    fetchPolicy: apolloFetchPolicy,
  });

  const EMPTY_VALIDATION: ProcedureValidation = {
    id: procedureId,
    editUrl: '',
    validationStatus: ProcedureValidationStatus.EMPTY,
  };
  const IN_PROGRESS_VALIDATION: ProcedureValidation = {
    id: procedureId,
    editUrl: '',
    validationStatus: ProcedureValidationStatus.IN_PROGRESS,
  };

  const procedureRef = useRef<ProcedureValidation>(EMPTY_VALIDATION);
  if (procedureId == null) {
    procedureRef.current = EMPTY_VALIDATION;
  } else if (
    procedureRef.current != null &&
    procedureRef.current.validationStatus !== ProcedureValidationStatus.EMPTY &&
    procedureRef.current.validationStatus !== ProcedureValidationStatus.IN_PROGRESS &&
    procedureRef.current.id === procedureId
  ) {
    // No-op: we already have the procedure we need.
  } else if (result.type === QueryResultType.SUCCESS) {
    procedureRef.current = parseProcedureForValidation(result.data);
  } else {
    // We are still waiting for the procedure to load.
    procedureRef.current = IN_PROGRESS_VALIDATION;
  }
  return procedureRef.current;
}
